import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SCREEN_SIZES } from 'style';
import useWindowSize from 'hooks/useWindowSize';
import { useInView } from 'react-intersection-observer';
import { useAnimation } from 'framer-motion6';
import useScrollBlock from 'hooks/useScrollBlock';
import ValueCard from '../ValueCard';
import { CardsContainer } from './styled';

const ValueCards = ({ values }) => {
  const cardWidth = 260;
  const marginRight = 40;

  const [flippedCardId, setFlippedCardId] = useState(0);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [scrollIsBlocked, setScrollIsBlocked] = useState(false);

  const { ref, inView } = useInView();
  const { width: windowWidth } = useWindowSize();
  const trackControls = useAnimation();
  const [blockScroll, allowScroll] = useScrollBlock();

  const flipCard = (id) => {
    const newCardIsClicked = id !== flippedCardId;

    if (newCardIsClicked) {
      setFlippedCardId(id);
    } else {
      setFlippedCardId(0);
    }
  };

  const getOffsets = (cards, windowWidth) => {
    const cardWidths = cards.map(() => cardWidth + marginRight);

    let offsets = cardWidths.map((card, index) =>
      cardWidths.slice(0, index + 1).reduce((a, b) => a + b, 0)
    );

    offsets.unshift(0);
    offsets.pop();

    if (windowWidth <= SCREEN_SIZES.mobileLarge) {
      offsets = offsets.map((offset) => {
        return offset - windowWidth / 2 + cardWidth / 2;
      });
    }

    return offsets;
  };

  const offsets = getOffsets(values, windowWidth);

  const getClosestSlide = (counts, goal) => {
    return counts.reduce((prev, curr) => {
      return Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev;
    });
  };

  const handleDrag = (target) => {
    const snapTarget = getClosestSlide(offsets, target * -1) * -1;

    const activeCardIndex = offsets.findIndex((offset) => offset === -snapTarget);

    setActiveCardIndex(activeCardIndex);

    return snapTarget;
  };

  useEffect(() => {
    const target = offsets[0];

    trackControls.start({ x: -target, transition: { duration: 0.01 } });
  }, []);

  const handleWindowScroll = () => {
    if (!scrollIsBlocked) {
      setScrollIsBlocked(true);

      setTimeout(() => {
        setScrollIsBlocked(false);
      }, [1500]);
    }
  };

  useEffect(() => {
    if (scrollIsBlocked) {
      blockScroll();
    } else {
      allowScroll();
    }
  }, [scrollIsBlocked]);

  return (
    <CardsContainer
      drag="x"
      animate={trackControls}
      onDragStart={handleWindowScroll}
      dragTransition={{
        power: 0.1,
        timeConstant: 200,
        modifyTarget: handleDrag
      }}
      ref={ref}
    >
      {values.map((value) => (
        <ValueCard
          key={value.id}
          id={value.id}
          name={value.name}
          description={value.description}
          frontBg={value.frontBg}
          backBg={value.backBg}
          length={values.length}
          flipCard={(id) => flipCard(id)}
          activeCardIndex={activeCardIndex}
          flippedCardId={flippedCardId}
          inView={inView}
          marginRight={marginRight}
          cardWidth={cardWidth}
        />
      ))}
    </CardsContainer>
  );
};

export default ValueCards;

ValueCards.propTypes = {
  values: PropTypes.array.isRequired
};
